/**
 * /devhub/traces — Execution traces (operator). Surface Layer (/01).
 *
 * REQ-001.91 DevHub UI rebuild · Phase 2 (already-live domains). The live ReWOO/execution trace stream
 * (`/api/control/traces` via `useControlTraces`) through the Phase-0 kit: a clickable OperatorDataGrid;
 * each row opens the OperatorDetailTearsheet with the full metadata payload. Operator-only.
 */

import { InlineNotification } from '@carbon/react';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import type { OperatorColumn, OperatorDetailMode } from '@contracts/devhub-kit.contract';
import { useEntitlements } from '@lib/persistence/useEntitlements';
import { useControlTraces, type ControlTraceRow } from '@lib/persistence/useRuntimeData';
import { AiAffordanceLabel, OperatorDataGrid, OperatorDetailTearsheet } from '../components/devhub-kit';

export const Route = createFileRoute('/devhub/traces')({ component: DevHubTracesPage });

const COLUMNS: OperatorColumn<ControlTraceRow>[] = [
  { key: 'phase', header: 'Phase' },
  { key: 'action', header: 'Action' },
  { key: 'status', header: 'Status', isStatus: true },
  { key: 'actor', header: 'Actor' },
  { key: 'created_at', header: 'Created' },
];

function DevHubTracesPage() {
  const { isElevated } = useEntitlements();
  const { traces, isLoading } = useControlTraces(100);
  const [selected, setSelected] = useState<ControlTraceRow | null>(null);
  const [mode, setMode] = useState<OperatorDetailMode>('view');

  if (!isElevated) {
    return (
      <main className="view">
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Operator access required"
          subtitle="Execution traces are operator-only."
        />
      </main>
    );
  }

  return (
    <main className="view">
      <header className="view__header">
        <h1 className="view__title">
          Traces <AiAffordanceLabel />
        </h1>
        <p className="view__subtitle">Live execution traces — ReWOO phases, actors and outcomes.</p>
      </header>

      <section className="view__section">
        <OperatorDataGrid
          ariaLabel="Execution traces"
          columns={COLUMNS}
          rows={traces}
          getRowId={(row) => row.id}
          onRowClick={(row) => {
            setSelected(row);
            setMode('view');
          }}
          loading={isLoading}
          emptyLabel="No execution traces recorded yet."
        />
      </section>

      <OperatorDetailTearsheet
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.phase} · ${selected.action}` : ''}
        subtitle={selected?.status ?? ''}
        mode={mode}
        onModeChange={setMode}
        fields={
          selected
            ? [
                { label: 'Phase', value: selected.phase },
                { label: 'Action', value: selected.action },
                { label: 'Status', value: selected.status },
                { label: 'Actor', value: selected.actor },
                { label: 'ActionFrame (client id)', value: selected.action_frame_client_id ?? '—' },
                { label: 'Created', value: selected.created_at },
                { label: 'Metadata', value: JSON.stringify(selected.metadata ?? {}, null, 2) },
              ]
            : []
        }
      />
    </main>
  );
}
